// AdminSetupFlowCard — shown after a school user pays ₹499 for "School Setup
// by Admin" (iter109 new flow) OR when on legacy admin-mode pre-iter109.
//
// Renders 5-step Odia instruction workflow:
//   1. Download the printable Setup Form (PDF)
//   2. Print it
//   3. Fill in EVERY detail in English AND Odia clearly
//   4. Take clear photos of the filled form
//   5. Upload the photos below + send via WhatsApp to admin
//
// Inline file upload (reuses SubmissionRecords) + WhatsApp share button.
import { useState } from "react";
import axios from "axios";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import SubmissionRecords from "@/components/poshan/SubmissionRecords";
import {
  Crown, FileDown, MessageCircle, Printer, PenLine, Camera,
  Upload, CheckCircle2, Sparkles, Hourglass,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_WA = process.env.REACT_APP_WHATSAPP_NUMBER || "918093414633";
const PDF_HREF = "/SchoolSetupForm.pdf";

function buildWaMessage(user) {
  return (
    `ନମସ୍କାର 🙏\n\n` +
    `ମୁଁ "School Setup by Admin" ସେବା ପାଇଁ ₹499 ପଇଠ କରିଛି।\n` +
    `ମୋ ବିଦ୍ୟାଳୟର ସମ୍ପୂର୍ଣ୍ଣ Setup ଆପଣ କରିଦିଅନ୍ତୁ — ମୁଁ Form ପୂରଣ କରି photo ପଠାଉଛି।\n\n` +
    `📚 ବିଦ୍ୟାଳୟ: ${user?.schoolName || ""}\n` +
    `🆔 UDISE: ${user?.udiseCode || ""}\n` +
    `🏷️ ବର୍ଗ: ${user?.schoolCategory || ""}\n` +
    `📱 ମୋ ମୋବାଇଲ୍: ${user?.mobile || ""}\n\n` +
    `ଧନ୍ୟବାଦ 🙏`
  );
}

function buildReminderMessage(user) {
  return (
    `ନମସ୍କାର 🙏\n\n` +
    `ମୁଁ Filled Setup Form upload କରିଛି। ଦୟାକରି ଶୀଘ୍ର Setup ସମ୍ପନ୍ନ କରିଦିଅନ୍ତୁ।\n\n` +
    `🏫 ବିଦ୍ୟାଳୟ: ${user?.schoolName || ""}\n` +
    `🆔 UDISE: ${user?.udiseCode || ""}\n\n` +
    `ଧନ୍ୟବାଦ 🙏`
  );
}

function waLink(user, reminder = false) {
  const text = reminder ? buildReminderMessage(user) : buildWaMessage(user);
  return `https://wa.me/${ADMIN_WA}?text=${encodeURIComponent(text)}`;
}

const STEPS = [
  {
    n: 1,
    icon: FileDown,
    titleEn: "Download Setup Form",
    titleOd: "ସେଟଅପ୍ ଫର୍ମ Download କରନ୍ତୁ",
    descOd: "ତଳର 'Download Form' button ଦବାଇ PDF Setup Form download କରନ୍ତୁ।",
  },
  {
    n: 2,
    icon: Printer,
    titleEn: "Print the Form",
    titleOd: "Form print କରନ୍ତୁ",
    descOd: "Download ହୋଇଥିବା Form କୁ ଗୋଟିଏ A4 paper ରେ print କରନ୍ତୁ।",
  },
  {
    n: 3,
    icon: PenLine,
    titleEn: "Fill ALL details clearly — English + Odia",
    titleOd: "ସବୁ ତଥ୍ୟ English ଓ ଓଡ଼ିଆ ଉଭୟରେ ସ୍ପଷ୍ଟ ଭାବେ ଲେଖନ୍ତୁ",
    descOd: "ସ୍କୁଲ୍ ବିବରଣୀ, ଭେଣ୍ଡର୍ ବିବରଣୀ, CCH ବିବରଣୀ — ଯାହା ଯାହା Form ରେ ଅଛି ସବୁ ସ୍ପଷ୍ଟ ଭାବେ ଲେଖନ୍ତୁ।",
  },
  {
    n: 4,
    icon: Camera,
    titleEn: "Take clear photos of the filled form",
    titleOd: "ପୂରଣ ହୋଇଥିବା Form ର photo ବା scan ତିଆରି କରନ୍ତୁ",
    descOd: "ସବୁ page ର ସ୍ପଷ୍ଟ photo ଉଠାନ୍ତୁ। ଖବର ସ୍ପଷ୍ଟ ଦେଖାଯିବ ତାହା ନିଶ୍ଚିତ କରନ୍ତୁ।",
  },
  {
    n: 5,
    icon: Upload,
    titleEn: "Upload below + Share on WhatsApp",
    titleOd: "ତଳେ Upload କରନ୍ତୁ + WhatsApp ଯୋଗେ ପଠାନ୍ତୁ",
    descOd: "ତଳର Upload section ରେ photo upload କରନ୍ତୁ ଏବଂ ଆମ WhatsApp ନଂ +91 80934 14633 କୁ ମଧ୍ୟ ପଠାନ୍ତୁ।",
  },
];

export default function AdminSetupFlowCard({ user }) {
  const { refreshMe } = useAuth();
  const [downloaded, setDownloaded] = useState(false);

  const onDownload = () => {
    setDownloaded(true);
    window.open(PDF_HREF, "_blank", "noopener,noreferrer");
    // Best-effort flag for backend so admin dashboard knows the school
    // has actually downloaded the form.
    axios.post(`${API}/auth/admin-form-shared`).catch(() => {});
  };

  return (
    <section
      data-testid="admin-setup-flow-card"
      className="mb-8 overflow-hidden rounded-2xl border-2 border-clay/40 bg-gradient-to-br from-amber-50 via-orange-50/40 to-amber-50 shadow-md"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 to-orange-600 px-5 py-5 text-white sm:px-7 sm:py-6">
        <div className="flex items-center gap-2 text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-white/90">
          <Crown className="h-3.5 w-3.5" /> Payment received · Setup by Admin
        </div>
        <h2 className="mt-1 font-heading text-xl font-bold sm:text-2xl" data-testid="admin-setup-headline">
          🙏 ଧନ୍ୟବାଦ! ଆମ Admin Team ଆପଣଙ୍କ ବିଦ୍ୟାଳୟର Setup କରିବେ
        </h2>
        <p className="mt-2 text-sm text-white/95">
          ଦୟାକରି ତଳର 5 ଟି ପଦକ୍ଷେପ ଅନୁସରଣ କରନ୍ତୁ। Setup ସମ୍ପୂର୍ଣ୍ଣ ହେଲା ପରେ ଆପଣଙ୍କ account 1 ଘଣ୍ଟା ମଧ୍ୟରେ ସ୍ୱୟଂଚାଳିତ verify ହୋଇଯିବ।
        </p>
      </div>

      {/* Steps */}
      <div className="px-5 py-5 sm:px-7 sm:py-6">
        <h3 className="mb-4 text-[0.75rem] font-bold uppercase tracking-[0.16em] text-clay-dark">
          📋 କେମିତି କରିବେ · How to complete (5 steps)
        </h3>
        <ol className="space-y-3" data-testid="admin-setup-steps">
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <li
                key={s.n}
                className="flex gap-3 rounded-xl border border-amber-200 bg-white p-4 shadow-sm"
                data-testid={`admin-setup-step-${s.n}`}
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 font-bold text-amber-700">
                  {s.n}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                    <div>
                      <p className="text-sm font-semibold text-ink-900">{s.titleEn}</p>
                      <p className="text-sm font-medium text-clay-dark">{s.titleOd}</p>
                    </div>
                  </div>
                  <p className="mt-1.5 text-xs text-ink-700">{s.descOd}</p>
                </div>
              </li>
            );
          })}
        </ol>

        {/* Action buttons */}
        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Button
            onClick={onDownload}
            className="h-12 bg-amber-600 text-white hover:bg-amber-700"
            data-testid="admin-setup-download-form-btn"
          >
            <FileDown className="mr-2 h-5 w-5" /> Download Setup Form
          </Button>
          <Button asChild className="h-12 bg-emerald-600 text-white hover:bg-emerald-700" data-testid="admin-setup-whatsapp-btn">
            <a href={waLink(user, false)} target="_blank" rel="noreferrer">
              <MessageCircle className="mr-2 h-5 w-5" /> Send via WhatsApp
            </a>
          </Button>
        </div>
        {downloaded && (
          <p className="mt-3 flex items-center gap-2 text-xs text-emerald-700" data-testid="admin-setup-download-confirmed">
            <CheckCircle2 className="h-4 w-4" /> Form downloaded — print it & fill all details in English + Odia.
          </p>
        )}
      </div>

      {/* File upload widget — reuses SubmissionRecords */}
      <div className="border-t-2 border-amber-200 bg-white px-5 py-5 sm:px-7 sm:py-6">
        <div className="mb-4 flex items-start gap-3">
          <Upload className="mt-0.5 h-5 w-5 text-amber-600" />
          <div>
            <h3 className="text-base font-semibold text-ink-900">Upload your filled form photos here</h3>
            <p className="mt-1 text-sm font-medium text-clay-dark">
              ଆପଣଙ୍କ ପୂରଣ ହୋଇଥିବା Form ର photo ତଳେ upload କରନ୍ତୁ
            </p>
            <p className="mt-1 text-xs text-ink-700">
              You can upload a single file or multiple files. JPG / PNG / PDF accepted.
            </p>
          </div>
        </div>
        <div data-testid="admin-setup-upload-widget">
          <SubmissionRecords />
        </div>
      </div>

      {/* Reminder + waiting state */}
      <div className="border-t border-amber-200 bg-amber-50/60 px-5 py-4 sm:px-7">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Hourglass className="h-5 w-5 text-amber-600 animate-pulse" />
            <p className="text-sm text-amber-900">
              <b>Admin will complete your setup within 24 hours.</b> Auto-verification starts 1 hour after they finish.
            </p>
          </div>
          <Button
            asChild
            variant="outline"
            size="sm"
            className="border-amber-400 text-amber-700 hover:bg-amber-100"
            data-testid="admin-setup-remind-btn"
          >
            <a href={waLink(user, true)} target="_blank" rel="noreferrer">
              <Sparkles className="mr-2 h-4 w-4" /> Remind Admin
            </a>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => refreshMe()} data-testid="admin-setup-refresh-btn">
            Refresh status
          </Button>
        </div>
      </div>
    </section>
  );
}
